/**
 * useSmartPoll — a polling hook that is safe for long-lived UI surfaces.
 *
 * Why this exists:
 *   Naive setInterval polling overwhelmed the backend when multiple tabs
 *   were open or when the user navigated away without unmounting the
 *   component — every 5 seconds each tab would fire requests even while
 *   backgrounded. This hook fixes four concrete issues:
 *
 *     1. **Page Visibility** — stops polling when the tab is hidden
 *        (document.hidden) and resumes instantly when it returns to
 *        the foreground. No CPU / network while the user is elsewhere.
 *
 *     2. **Max-1 concurrent request** — a small `inFlightRef` gate
 *        prevents a slow response from causing pile-up (e.g. if the
 *        server is briefly busy and the fetcher takes 8 s while the
 *        interval is 5 s, we skip the next tick instead of stacking).
 *
 *     3. **Failure backoff** — on any thrown error the next poll is
 *        delayed by `errorBackoffMs` (default 30 s) to give the server
 *        room to breathe. Normal polling resumes once a request succeeds.
 *
 *     4. **Auto-stop when idle** — when `shouldPoll(data)` returns
 *        `false` the interval is torn down entirely. No "leaking"
 *        intervals firing every 10 s for data that has already
 *        reached a terminal state.
 *
 * The caller supplies the fetcher + a `shouldPoll` predicate. The hook
 * keeps its own state ref so the fetcher is free to mutate the UI
 * state however it likes (typically with a diff-aware setter to avoid
 * re-rendering unchanged rows — see StockDetailPage / ReportsPage).
 *
 * Usage:
 *   useSmartPoll({
 *     fetch: () => loadReports(),
 *     shouldPoll: () => reports.some((r) => r.analysis_status === "pending"),
 *     intervalMs: 10_000,
 *     errorBackoffMs: 30_000,
 *     enabled: true,
 *   });
 */
import { useEffect, useRef } from "react";

export const useSmartPoll = ({
  fetch: fetcher,
  shouldPoll,
  intervalMs = 10_000,
  errorBackoffMs = 30_000,
  enabled = true,
} = {}) => {
  const timeoutRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  const shouldPollRef = useRef(shouldPoll);

  // Keep the latest callbacks in refs so the poll loop never closes
  // over a stale `reports` / handler. This lets the caller update
  // `reports` via setState without re-creating the interval.
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);
  useEffect(() => { shouldPollRef.current = shouldPoll; }, [shouldPoll]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      return undefined;
    }

    let cancelled = false;

    const schedule = (delay) => {
      if (cancelled || !mountedRef.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled || !mountedRef.current) return;
      // Skip if tab backgrounded — resume will be triggered by
      // visibilitychange handler below.
      if (typeof document !== "undefined" && document.hidden) {
        schedule(intervalMs);
        return;
      }
      // Skip if we already have an in-flight call.
      if (inFlightRef.current) {
        schedule(intervalMs);
        return;
      }
      // Stop entirely when caller no longer wants to poll.
      const should = shouldPollRef.current ? shouldPollRef.current() : true;
      if (!should) {
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        return;
      }
      inFlightRef.current = true;
      let failed = false;
      try {
        await fetcherRef.current?.();
      } catch (_e) {
        failed = true;
      } finally {
        inFlightRef.current = false;
      }
      schedule(failed ? errorBackoffMs : intervalMs);
    };

    // First tick — slightly delayed so the caller's initial load has a
    // chance to complete before the first background refresh.
    schedule(intervalMs);

    const onVisibility = () => {
      if (cancelled || !mountedRef.current) return;
      if (!document.hidden) {
        // User returned to the tab — fire a fresh poll straight away.
        schedule(200);
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // We intentionally only depend on the config knobs — the fetcher
    // and predicate are read via refs so the caller doesn't need to
    // memoise them.
  }, [enabled, intervalMs, errorBackoffMs]);
};

export default useSmartPoll;
